const RESERVED_FIRST_SEGMENTS = new Set([
  'change-requests',
  'spaces',
  'releases',
  'edit',
]);

export interface InternalDocLink {
  spaceSlug: string;
  pageSlug: string;
}

/**
 * Recognises documentation page URLs an author might paste into markdown.
 * Accepts both the natural form (`/documentation/<space>/<page>`) and the
 * admin edit form (`/documentation/edit/<space>/<page>`). Returns null for
 * external URLs, app routes that aren't a page, and anything malformed —
 * those should fall through to the browser's default link behaviour.
 *
 * The backend normalises every `contentMd` it returns so the FE only ever
 * sees canonical hrefs in the reader and editor — Obsidian-style relative
 * paths like `./foo.md` and `../space/bar.md` get rewritten server-side
 * before they reach this parser.
 */
export function parseInternalDocLink(href: string): InternalDocLink | null {
  if (!href) return null;

  let path: string;
  try {
    const origin =
      typeof window !== 'undefined'
        ? window.location.origin
        : 'http://localhost';
    const url = new URL(href, origin);
    if (
      typeof window !== 'undefined' &&
      url.origin !== window.location.origin
    ) {
      return null;
    }
    path = url.pathname;
  } catch {
    if (!href.startsWith('/')) return null;
    path = href.split(/[?#]/)[0];
  }

  const match = /^\/documentation\/(?:edit\/)?([^/]+)\/([^/]+)\/?$/.exec(path);
  if (!match) return null;
  const [, spaceSlug, pageSlug] = match;
  if (RESERVED_FIRST_SEGMENTS.has(spaceSlug)) return null;
  return { spaceSlug, pageSlug };
}
